import { useTranslation } from 'react-i18next';
import { AppSkeleton } from '@bliks-io/app-sdk/react';
import { useInstalledApps } from '@/api/queries/useInstalledApps';
import { findInstalledApp } from '@/lib/app-registry';
import { getAppComponent } from '@/lib/app-loader';
import { AppContextBridge } from '@/lib/app-context-bridge';
import { QueryErrorRetry } from '@/components/query-error-retry/QueryErrorRetry';

/**
 * Mounts one installed app. The customer area (`/$slug/apps/$`) and the
 * partner area (`/partner/apps/$`) share it: the host is the same, only the
 * layout around it differs. Which apps a tenant has comes from the installed
 * apps list, so an app the organization does not hold is "not found", not an
 * empty page.
 */
export function AppHostPage({ appId }: { appId: string }) {
  const { t } = useTranslation();
  const { data: apps, isPending, isError, refetch } = useInstalledApps();
  const app = findInstalledApp(apps, appId);
  const AppComponent = app ? getAppComponent(app.appId) : null;

  if (isPending) return <AppSkeleton />;

  if (isError && !apps) return <QueryErrorRetry onRetry={refetch} className="h-full" />;

  if (!app || !AppComponent) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">{t('common.Apps.notFound')}</p>
      </div>
    );
  }

  return <AppContextBridge app={app} AppComponent={AppComponent} />;
}
